/**
 * ThemesV2ListView - List view for V2 themes with lazy loading
 *
 * Displays V2 themes as cards in a grid with actions (edit, clone, delete).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { ThemeV2Card } from './ThemeV2Card';
import type { ThemeV2 } from '../types/themesV2.types';

export interface ThemesV2ListViewProps {
  /** List of V2 themes to display */
  themes: ThemeV2[];
  /** Whether themes are loading */
  loading: boolean;
  /** Whether there are more themes to load */
  hasMore: boolean;
  /** Callback to load more themes */
  onLoadMore: () => void;
  /** Callback when Edit action clicked */
  onEdit: (themeId: string) => void;
  /** Callback when Clone action clicked */
  onClone: (themeId: string) => void;
  /** Callback when Delete action clicked (after confirmation) */
  onDelete: (themeId: string) => void;
}

/**
 * ThemesV2ListView - Grid of V2 theme cards with lazy loading
 */
export const ThemesV2ListView: React.FC<ThemesV2ListViewProps> = ({
  themes,
  loading,
  hasMore,
  onLoadMore,
  onEdit,
  onClone,
  onDelete,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [themeToDelete, setThemeToDelete] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Handle delete button click - show confirmation
  const handleDeleteClick = useCallback((themeId: string) => {
    setThemeToDelete(themeId);
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete
  const handleDeleteConfirm = useCallback(() => {
    if (themeToDelete) {
      onDelete(themeToDelete);
    }
    setDeleteDialogOpen(false);
    setThemeToDelete(null);
  }, [themeToDelete, onDelete]);

  // Cancel delete
  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setThemeToDelete(null);
  }, []);

  // Set up intersection observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const current = loadMoreRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  // Loading state - initial load
  if (loading && themes.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Empty state
  if (!loading && themes.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          gap: 2,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No V2 themes found. Create your first V2 theme to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3} data-testid="v2-themes-grid">
        {themes.map((theme) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={theme.id}>
            <ThemeV2Card
              theme={theme}
              onEdit={onEdit}
              onClone={onClone}
              onDelete={handleDeleteClick}
            />
          </Grid>
        ))}
      </Grid>

      {/* Loading more indicator */}
      {hasMore && (
        <Box
          ref={loadMoreRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading...' : 'Loading more...'}
          </Typography>
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete V2 Theme</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this V2 theme? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" data-testid="v2-theme-delete-confirm">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThemesV2ListView;
